import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Hls from "hls.js";
import VTBLikeButton from "./VTBLikeButton";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useAppSelector } from "../../store/hooks";
import Tooltip from "@mui/material/Tooltip";
import { updateNcgVideoUsage } from "./VTB.API";

interface VTBVideoPlayerProps {
  selectedVideo: any;
  apiBase: string;
  apiKey: string;
  hlsVideoId: string;
  onBack: () => void;
  onVideoChange?: (video: any) => void;
  onCommentClick?: () => void;
}

const VTBVideoPlayer: React.FC<VTBVideoPlayerProps> = ({
  selectedVideo,
  apiBase,
  apiKey,
  hlsVideoId,
  onBack,
  onCommentClick,
}) => {
  const videoPlayerRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  console.log("selectedVideo =====> ", selectedVideo?.video_id);

  const [watchProgress, setWatchProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [playerError, setPlayerError] = useState<string | null>(null);

  const customerDetails = useAppSelector(
    (state) => state.customer.customerDetails,
  );
  const customerId = sessionStorage.getItem("informaticscustomer_id");
  const usermasterId = sessionStorage.getItem("user_id");
  const consortiaID = customerDetails?.consortiamaster_id ?? 0;
  const sessionID = sessionStorage.getItem("session_key");

  //   const customer_id = customerId ?? customerDetails;

  const countOfCommets = useAppSelector(
    (state: any) => state.ncgDetails?.comment_count,
  );

  useEffect(() => {
    const videoElement = videoPlayerRef.current;

    if (!videoElement || !selectedVideo) {
      return;
    }

    // const videoId = hlsVideoId;
    const videoId = selectedVideo?.video_id;

    const manifestUrl = `${apiBase}/hls/${encodeURIComponent(videoId)}`;

    setPlayerError(null);
    setWatchProgress(0);
    setIsCompleted(false);
    // Destroy previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    // Reset video element
    videoElement.pause();
    videoElement.removeAttribute("src");
    videoElement.load();

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxBufferLength: 60,
        maxMaxBufferLength: 600,

        xhrSetup: (xhr, url) => {
          if (!url.includes("/hls-segment/")) {
            xhr.setRequestHeader("x-api-key", apiKey);
          }
        },
      });

      hlsRef.current = hls;

      hls.loadSource(manifestUrl);
      hls.attachMedia(videoElement);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoElement.play().catch((error) => {
          console.warn("Autoplay blocked:", error);
        });
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.error("HLS error:", data);

        if (!data.fatal) {
          return;
        }

        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad();
            break;

          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError();
            break;

          default:
            setPlayerError(
              "This video cannot be played. Please try again later.",
            );

            hls.destroy();
            hlsRef.current = null;
            break;
        }
      });
    } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
      videoElement.src = manifestUrl;

      const handleLoadedMetadata = () => {
        videoElement.play().catch((error) => {
          console.warn("Autoplay blocked:", error);
        });
      };

      const handleVideoError = () => {
        setPlayerError("This video cannot be played. Please try again later.");
      };

      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);

      videoElement.addEventListener("error", handleVideoError);

      return () => {
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata,
        );

        videoElement.removeEventListener("error", handleVideoError);

        videoElement.pause();
        videoElement.removeAttribute("src");
        videoElement.load();
      };
    } else {
      setPlayerError("HLS playback is not supported in this browser.");
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      videoElement.pause();
      videoElement.removeAttribute("src");
      videoElement.load();
    };
  }, [selectedVideo, apiBase, apiKey, hlsVideoId]);

  useEffect(() => {
    const videoElement = videoPlayerRef.current;

    if (!videoElement) {
      return;
    }

    const handleTimeUpdate = () => {
      if (!videoElement.duration) {
        return;
      }

      const progress = Math.round(
        (videoElement.currentTime / videoElement.duration) * 100,
      );

      setWatchProgress(Math.min(100, Math.max(0, progress)));
    };

    const handleEnded = () => {
      setWatchProgress(100);
      setIsCompleted(true);
    };

    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("ended", handleEnded);

    return () => {
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);

      videoElement.removeEventListener("ended", handleEnded);
    };
  }, [selectedVideo]);

  useEffect(() => {
    const videoElement = videoPlayerRef.current;

    if (!videoElement || !selectedVideo?.article_id) {
      return;
    }

    let watchedSeconds = 0;
    let usageTimer: number | null = null;

    const sendVideoUsage = async () => {
      try {
        await updateNcgVideoUsage({
          profile_id: usermasterId,
          customer_id: customerId,
          consortia_id: consortiaID,
          article_id: selectedVideo.article_id,
          session_id: sessionID,
          duration: null,
        });

        console.log(
          `Video usage API called for article ${selectedVideo.article_id}`,
        );
      } catch (error) {
        console.error("Failed to update video usage:", error);
      }
    };

    const startTracking = () => {
      // Prevent multiple timers
      if (usageTimer !== null) {
        return;
      }

      usageTimer = window.setInterval(() => {
        // Safety check
        if (videoElement.paused || videoElement.ended) {
          return;
        }

        watchedSeconds += 1;

        // Every 30 seconds of actual playback
        if (watchedSeconds === 30) {
          watchedSeconds = 0;

          sendVideoUsage();
        }
      }, 1000);
    };

    const stopTracking = () => {
      if (usageTimer !== null) {
        window.clearInterval(usageTimer);
        usageTimer = null;
      }
    };

    // Video started playing
    const handlePlay = () => {
      startTracking();
    };

    // User paused the video
    const handlePause = () => {
      stopTracking();
    };

    // Video finished
    const handleEnded = () => {
      stopTracking();
    };

    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("pause", handlePause);
    videoElement.addEventListener("ended", handleEnded);

    return () => {
      stopTracking();

      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("pause", handlePause);
      videoElement.removeEventListener("ended", handleEnded);
    };
  }, [selectedVideo]);

  if (!selectedVideo) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">No video selected.</Typography>
      </Box>
    );
  }

  const getDateOnly = (dateString: string): string => {
    return dateString.split("T")[0];
  };

  const getMonthYear = (dateString?: string | null): string => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          if (videoPlayerRef.current) {
            videoPlayerRef.current.pause();
            videoPlayerRef.current.currentTime = 0;
          }

          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }

          onBack();
        }}
        sx={{
          mb: 2,
          textTransform: "none",
          fontWeight: "bold",
        }}
      >
        Back to Video Library
      </Button>

      <Box
        sx={{
          bgcolor: "white",
          p: 3,
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                width: "100%",
                paddingTop: "56.25%",
                position: "relative",
                bgcolor: "black",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <video
                ref={videoPlayerRef}
                controls
                playsInline
                preload="metadata"
                poster={selectedVideo?.thumbnailUrl}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
              />

              {playerError && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#111",
                    px: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body1" sx={{ color: "#fff" }}>
                    This video cannot be played here.
                  </Typography>

                  <Typography variant="body2" sx={{ color: "#aaa" }}>
                    Please try again later.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  color: isCompleted ? "success.main" : "text.secondary",
                }}
              >
                {isCompleted
                  ? "Video Completed"
                  : `Video Progress: ${watchProgress}%`}
              </Typography>

              <LinearProgress
                variant="determinate"
                value={watchProgress}
                color={isCompleted ? "success" : "primary"}
                sx={{
                  height: 8,
                  borderRadius: 4,
                }}
              />
            </Box>

            <Typography
              variant="h5"
              color="primary"
              sx={{
                fontWeight: "bold",
                mt: 3,
                mb: 1,
              }}
            >
              {selectedVideo?.title}
            </Typography>

            <Divider sx={{ my: 2 }} />
            {/* <Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "column",
                  md: "column",
                  lg: "row",
                },
                alignItems: {
                  xs: "flex-start",
                  lg: "center",
                },
                gap: 2,
                color: "text.secondary",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon fontSize="small" />
                <Typography variant="body2">
                  {selectedVideo?.age_group}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarTodayIcon fontSize="small" />
                <Typography variant="body2">
                  {getMonthYear(selectedVideo?.dateofpublication)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2">
                  {selectedVideo?.presenting_centre}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">{`Stage of Cancer: ${selectedVideo?.stage_of_cancer}`}</Typography>
              </Box>
              <VTBLikeButton articleId={selectedVideo?.article_id} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.secondary",
                }}
              >
                <ChatBubbleOutlineIcon
                  sx={{
                    fontSize: 20,
                  }}
                />

                <Typography variant="body2">{countOfCommets}</Typography>
              </Box>
            </Box> */}

            <Box
              sx={{
                color: "text.secondary",
              }}
            >
              {/* Video Information */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "column",
                    md: "column",
                    lg: "row",
                  },
                  alignItems: {
                    xs: "flex-start",
                    lg: "center",
                  },
                  gap: 2,
                }}
              >
                {/* Age Group */}
                {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  <Typography variant="body2">
                    {selectedVideo?.age_group}
                  </Typography>
                </Box> */}

                <Tooltip title="Age Group" arrow placement="top">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2">
                      {selectedVideo?.age_group}
                    </Typography>
                  </Box>
                </Tooltip>

                {/* Publication Date */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarTodayIcon fontSize="small" />
                  <Typography variant="body2">
                    {getMonthYear(selectedVideo?.dateofpublication)}
                  </Typography>
                </Box>

                {/* Presenting Centre */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2">
                    {selectedVideo?.presenting_centre}
                  </Typography>
                </Box>

                {/* Stage of Cancer */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">
                    {`Stage of Cancer: ${selectedVideo?.stage_of_cancer}`}
                  </Typography>
                </Box>
              </Box>

              {/* Like + Comments - Separate Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mt: 1.5,
                }}
              >
                {/* Like */}
                <VTBLikeButton articleId={selectedVideo?.article_id} />

                {/* Comments */}
                <Box
                  onClick={onCommentClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "text.secondary",
                    cursor: "pointer",
                  }}
                >
                  <ChatBubbleOutlineIcon
                    sx={{
                      fontSize: 20,
                    }}
                  />

                  <Typography variant="body2">{countOfCommets}</Typography>
                </Box>
              </Box>
            </Box>
            {/* <Box mt={2}>
              <VTBLikeButton articleId={selectedVideo?.article_id} />
            </Box> */}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default VTBVideoPlayer;
